import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bot, Shield, Zap, TrendingUp, Settings, LogOut, Megaphone } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
import { supabase } from '../lib/supabase-client';
import LanguageSelector from '../components/LanguageSelector';
import AIAnalysis from '../components/AIAnalysis';
import toast from 'react-hot-toast';

type Announcement = {
  id: string;
  title: string;
  content: string;
  type: 'ad' | 'message';
  is_active: boolean;
  created_at: string;
};

type TradingBot = {
  id: string;
  name: string;
  description: string;
  risk_level: 'low' | 'medium' | 'high';
  pnl_percentage: number;
  running_days: number;
  is_active: boolean;
  show_on_public: boolean;
};

function PublicHome() {
  const { t } = useLanguage();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [publicBots, setPublicBots] = useState<TradingBot[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectingBot, setSelectingBot] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkUser();
    Promise.all([
      loadAnnouncements(),
      loadPublicBots()
    ]).finally(() => setLoading(false));
  }, []);

  const checkUser = async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      
      if (session?.user) {
        setUser(session.user);
        
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', session.user.id)
          .single();
          
        setIsAdmin(profile?.is_admin || false);
      }
    } catch (error) {
      console.error('Error checking user:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setIsAdmin(false);
      localStorage.removeItem('supabase.auth.token');
      toast.success('Logged out successfully');
      navigate('/', { replace: true });
    } catch (error: any) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    }
  };

  const handleSelectBot = async (botId: string) => {
    if (!user?.id) {
      navigate('/login');
      return;
    }

    if (selectingBot) return;
    setSelectingBot(true);

    try {
      // First check if selection exists
      const { data: existingSelection, error: checkError } = await supabase
        .from('user_selections')
        .select('*')
        .eq('user_id', user.id)
        .eq('bot_id', botId)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingSelection) {
        // Update existing selection
        const { error: updateError } = await supabase
          .from('user_selections')
          .update({ 
            is_selected: true,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id)
          .eq('bot_id', botId);

        if (updateError) throw updateError;
      } else {
        // Create new selection
        const { error: insertError } = await supabase
          .from('user_selections')
          .insert([{ 
            user_id: user.id, 
            bot_id: botId,
            is_selected: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }]);

        if (insertError) throw insertError;
      }

      // Create or update bot status
      const { error: statusError } = await supabase
        .from('user_bot_status')
        .upsert([{
          user_id: user.id,
          bot_id: botId,
          is_selected: true,
          is_trading: false,
          updated_at: new Date().toISOString()
        }], {
          onConflict: 'user_id,bot_id'
        });

      if (statusError) throw statusError;

      toast.success('Bot selected successfully');
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error selecting bot:', error);
      toast.error('Failed to select bot');
    } finally {
      setSelectingBot(false);
    }
  };

  const loadAnnouncements = async () => {
    try {
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAnnouncements(data || []);
    } catch (error: any) {
      console.error('Error loading announcements:', error);
    }
  };

  const loadPublicBots = async () => {
    try {
      const { data, error } = await supabase
        .from('trading_bots')
        .select('*')
        .eq('is_active', true)
        .eq('show_on_public', true)
        .order('pnl_percentage', { ascending: false });

      if (error) throw error;
      setPublicBots(data || []);
    } catch (error: any) {
      console.error('Error loading public bots:', error);
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'low':
        return 'bg-green-500/10 border-green-500/20';
      case 'medium':
        return 'bg-yellow-500/10 border-yellow-500/20';
      case 'high':
        return 'bg-red-500/10 border-red-500/20';
      default:
        return 'bg-gray-500/10 border-gray-500/20';
    }
  };

  const getRiskIconColor = (risk: string) => {
    switch (risk) {
      case 'low':
        return 'text-green-400';
      case 'medium':
        return 'text-yellow-400';
      case 'high':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-900 via-blue-700 to-blue-500">
      {/* Header */}
      <header className="bg-black/90 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between h-14 md:h-16 items-center px-4">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex-shrink-0 flex items-center group">
                <Bot className="h-5 w-5 md:h-6 md:w-6 text-white group-hover:text-cyan-400 transition-colors" />
                <div className="flex items-baseline">
                  <span className="ml-2 text-lg md:text-xl font-semibold text-white group-hover:text-cyan-400 transition-colors">ÁiX</span>
                  <span className="ml-2 text-xs text-gray-400">Beta202502</span>
                </div>
              </Link>
              <div className="hidden md:flex space-x-4">
                <Link
                  to="/trading-bots"
                  className="flex items-center text-white hover:text-cyan-400 transition-colors text-sm md:text-base"
                >
                  <TrendingUp className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                  <span>{t('nav.tradingBots')}</span>
                </Link>
              </div>
            </div>
            
            {/* Right side navigation */}
            <div className="flex items-center space-x-4">
              {user ? (
                <>
                  {isAdmin ? (
                    <Link
                      to="/admin"
                      className="flex items-center text-white hover:text-cyan-400 transition-colors text-sm md:text-base"
                    >
                      <Shield className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                      <span>Dashboard</span>
                    </Link>
                  ) : (
                    <Link
                      to="/dashboard"
                      className="flex items-center text-white hover:text-cyan-400 transition-colors text-sm md:text-base"
                    >
                      <Bot className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                      <span>Dashboard</span>
                    </Link>
                  )}
                  <Link
                    to="/settings"
                    className="flex items-center text-white hover:text-cyan-400 transition-colors text-sm md:text-base"
                  >
                    <Settings className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                    <span>Settings</span>
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center text-sm text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    <LogOut className="h-4 w-4 mr-1" />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-white hover:text-cyan-400 transition-colors text-sm font-medium">
                    {t('auth.login')}
                  </Link>
                  <LanguageSelector />
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Announcements Section */}
          {user && announcements.length > 0 && (
            <section className="py-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {announcements.map((announcement) => (
                  <div
                    key={announcement.id}
                    className={`bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border ${
                      announcement.type === 'ad' ? 'border-purple-500/20' : 'border-cyan-500/20'
                    }`}
                  >
                    <div className="flex items-start space-x-4">
                      <Megaphone className={`h-6 w-6 flex-shrink-0 ${
                        announcement.type === 'ad' ? 'text-purple-400' : 'text-cyan-400'
                      }`} />
                      <div>
                        <h3 className="text-xl font-semibold text-white mb-2">
                          {announcement.title}
                        </h3>
                        <p className="text-gray-300">
                          {announcement.content}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Hero Section - Only show for non-logged in users */}
          {!user && (
            <>
              <section className="py-16 sm:py-24">
                <div className="text-center">
                  <h1 className="text-4xl tracking-tight font-extrabold text-white sm:text-5xl md:text-6xl">
                    <span className="block">{t('hero.title')}</span>
                    <span className="block text-cyan-400">{t('hero.subtitle')}</span>
                  </h1>
                  <p className="mt-3 max-w-md mx-auto text-base text-gray-300 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
                    {t('hero.description')}
                  </p>
                  <div className="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
                    <div className="rounded-md shadow">
                      <Link
                        to="/login"
                        className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-cyan-500 hover:bg-cyan-600 md:py-4 md:text-lg md:px-10 transition-colors"
                      >
                        {t('hero.cta')}
                      </Link>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-gray-400">
                    {t('disclaimer')}
                  </p>
                </div>
              </section>

              {/* Features Section - Only show for non-logged in users */}
              <section className="py-12 sm:py-16">
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="pt-6">
                    <div className="flow-root bg-blue-900/40 backdrop-blur-sm rounded-lg px-6 pb-8">
                      <div className="-mt-6">
                        <div>
                          <span className="inline-flex items-center justify-center p-3 bg-cyan-500 rounded-md shadow-lg">
                            <Bot className="h-6 w-6 text-white" />
                          </span>
                        </div>
                        <h3 className="mt-8 text-lg font-medium text-white tracking-tight">{t('features.ai.title')}</h3>
                        <p className="mt-5 text-base text-gray-300">
                          {t('features.ai.description')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-6">
                    <div className="flow-root bg-blue-900/40 backdrop-blur-sm rounded-lg px-6 pb-8">
                      <div className="-mt-6">
                        <div>
                          <span className="inline-flex items-center justify-center p-3 bg-cyan-500 rounded-md shadow-lg">
                            <Shield className="h-6 w-6 text-white" />
                          </span>
                        </div>
                        <h3 className="mt-8 text-lg font-medium text-white tracking-tight">{t('features.risk.title')}</h3>
                        <p className="mt-5 text-base text-gray-300">
                          {t('features.risk.description')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-6">
                    <div className="flow-root bg-blue-900/40 backdrop-blur-sm rounded-lg px-6 pb-8">
                      <div className="-mt-6">
                        <div>
                          <span className="inline-flex items-center justify-center p-3 bg-cyan-500 rounded-md shadow-lg">
                            <Zap className="h-6 w-6 text-white" />
                          </span>
                        </div>
                        <h3 className="mt-8 text-lg font-medium text-white tracking-tight">{t('features.execution.title')}</h3>
                        <p className="mt-5 text-base text-gray-300">
                          {t('features.execution.description')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Public Trading Bots Section - Only show for non-logged in users */}
              {publicBots.length > 0 && (
                <section className="py-12 sm:py-16">
                  <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-white">{t('tradingBots.topPerforming')}</h2>
                    <p className="mt-4 text-lg text-gray-300">
                      {t('tradingBots.topPerformingDesc')}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {publicBots.map((bot) => (
                      <div
                        key={bot.id}
                        className={`p-6 rounded-lg border ${getRiskColor(bot.risk_level)} backdrop-blur-sm`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <Bot className={`h-8 w-8 ${getRiskIconColor(bot.risk_level)}`} />
                            <div>
                              <h3 className="text-lg font-semibold text-white">{bot.name}</h3>
                              <div className="flex items-center space-x-2">
                                <p className="text-sm text-gray-300 capitalize">
                                  {t(`tradingBots.riskLevels.${bot.risk_level}`)}
                                </p>
                                <span className="text-sm px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
                                  {t('tradingBots.status.active')}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <p className="text-gray-300 text-sm mb-4">{bot.description}</p>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-black/20 rounded-lg p-3">
                            <p className="text-sm text-gray-400">{t('tradingBots.metrics.pnl')}</p>
                            <p className={`text-xl font-bold ${bot.pnl_percentage >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {bot.pnl_percentage >= 0 ? '+' : ''}{bot.pnl_percentage}%
                            </p>
                          </div>
                          <div className="bg-black/20 rounded-lg p-3">
                            <p className="text-sm text-gray-400">{t('tradingBots.metrics.running')}</p>
                            <p className="text-xl font-bold text-cyan-400">
                              {bot.running_days}{t('tradingBots.metrics.days')}
                            </p>
                          </div>
                        </div>

                        <div className="mt-4 flex justify-end">
                          <Link
                            to="/login"
                            className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors"
                          >
                            {t('auth.login')}
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}

          {/* AI Analysis Section */}
          <section className="py-12 sm:py-16">
            <AIAnalysis />
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white mt-auto">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col space-y-4">
            <div className="flex justify-between items-center">
              <div className="relative">
                <LanguageSelector />
              </div>
              <div className="flex space-x-4">
                <Link to="/privacy-policy" className="text-xs hover:text-blue-300">{t('footer.privacy')}</Link>
                <Link to="/terms-of-service" className="text-xs hover:text-blue-300">{t('footer.terms')}</Link>
                <a href="https://t.me/teamiccc" target="_blank" rel="noopener noreferrer" className="text-xs hover:text-blue-300">{t('footer.contact')}</a>
              </div>
            </div>
            <div className="flex justify-center">
              <p className="text-xs text-gray-500">© 2025 AIX. {t('footer.rights')}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PublicHome;
